import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { withFragments } from '@sorare/core/src/lib/gql';

import { RuleHelperFnReturnType } from 'components/so5/Rules/types';
import { getCompetitionPictureUrl } from 'lib/so5';

import {
  getCompetitionsRuleDataFromCompetitions_competition,
  getCompetitionsRule_so5Rules,
} from './__generated__/getCompetitionsRule.graphql';

export const getCompetitionsRuleDataFromCompetitions = withFragments(
  (competitions: getCompetitionsRuleDataFromCompetitions_competition[]) => ({
    label: <FormattedMessage {...fantasy.playersPermittedFrom} />,
    description: (
      <Vertical as="span" gap={0}>
        {competitions.map(competition => {
          const competitionPictureUrl = getCompetitionPictureUrl(competition);

          return (
            <Horizontal key={competition.slug} as="span">
              {competitionPictureUrl && (
                <img
                  src={competitionPictureUrl}
                  alt=""
                  width="16"
                  height="16"
                />
              )}
              {competition.displayName}
            </Horizontal>
          );
        })}
      </Vertical>
    ),
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 7.77273H6.82609V10.5H9.17391V7.77273H11V7.09091L8 4.5L5 7.09091V7.77273Z"
          fill="var(--c-rivals-win)"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1 3L8 0L15 3V7.35742C15 10.2283 13.4617 12.879 10.9691 14.3034L8 16L5.03089 14.3034C2.53829 12.879 1 10.2283 1 7.35742V3ZM2.5 7.35742V3.98909L8 1.63195L13.5 3.98909V7.35742C13.5 9.68999 12.2501 11.8437 10.2249 13.001L8 14.2724L5.7751 13.001C3.74986 11.8437 2.5 9.68999 2.5 7.35742Z"
          fill="var(--c-rivals-win)"
        />
      </svg>
    ),
  }),
  {
    competition: gql`
      fragment getCompetitionsRuleDataFromCompetitions_competition on Competition {
        slug
        displayName
        ...getCompetitionPictureUrl_competition
      }
      ${getCompetitionPictureUrl.fragments.competition}
    ` as TypedDocumentNode<getCompetitionsRuleDataFromCompetitions_competition>,
  }
);

export const getCompetitionsRule = withFragments(
  (
    rules: getCompetitionsRule_so5Rules,
    error: string
  ): RuleHelperFnReturnType => {
    const rule = rules.competitions;
    if (!rule?.length) {
      return null;
    }

    return {
      id: 'competitions',
      ...getCompetitionsRuleDataFromCompetitions(rule),
      disclaimer: {
        id: 'Rules.competitions.disclaimer',
        defaultMessage:
          'Some games may be subject to a delayed data coverage which could result in only a Player’s Decisive Score counting towards your final score. All impacted games are listed in the “Matches” section. Please check our Game Rules and Help Center for more information.',
      },
      error,
    };
  },
  {
    so5Rules: gql`
      fragment getCompetitionsRule_so5Rules on So5Rules {
        id
        competitions {
          slug
          displayName
          ...getCompetitionsRuleDataFromCompetitions_competition
        }
      }
      ${getCompetitionsRuleDataFromCompetitions.fragments.competition}
    ` as TypedDocumentNode<getCompetitionsRule_so5Rules>,
  }
);
